<template>
  <Dialog :open="modelValue" @update:open="val => !val && closeAdjustmentModal()">
    <DialogScrollContent class="sm:tw-max-w-[90vw] xl:tw-max-w-[75rem]">
      <DialogHeader>
        <DialogTitle>{{ t('payments.forms.allocate_amount') }}</DialogTitle>
      </DialogHeader>

      <div class="tw-overflow-y-auto tw-max-h-[60vh] tw-py-4">
        <Accordion type="single" collapsible default-value="item-0" class="tw-w-full tw-rounded-md">
          <AccordionItem
            v-for="(data, index) in props.paymentsData"
            :key="index"
            :value="`item-${index}`"
          >
            <AccordionTrigger class="tw-bg-gray-100 tw-text-black tw-px-4">
              <p class="tw-mb-0" v-if="data.excessAmount > 0">
                {{ t('payment.titles.payment_amount') }} "{{ n(+data.payment.amount, 'currency') }}"
                {{ t('payment.titles.warn_amount_exceeds_orders') }}
              </p>
              <p class="tw-mb-0" v-else>
                {{ t('payment.titles.payment_amount') }} "{{ n(+data.payment.amount, 'currency') }}"
                {{ t('payment.titles.warn_payment_less_than_orders') }}
              </p>
            </AccordionTrigger>
            <AccordionContent>
              <div class="tw-w-full tw-mt-4 tw-px-4">
                <adjustment-table
                  :payment-id="data.payment.id"
                  :payment-amount="data.payment.amount"
                  :list="data.payment.orders_verbose"
                  :excess-amount="data.excessAmount"
                  @adjusted="updateAdjustment"
                ></adjustment-table>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <!-- Validation Message -->
      <Alert v-if="!allValid" variant="destructive" class="tw-mx-0">
        <AlertDescription>
          <span class="tw-font-semibold">Note:</span>
          {{ validationMessage }}
        </AlertDescription>
      </Alert>

      <DialogFooter>
        <Button variant="secondary" @click="closeAdjustmentModal">
          {{ t('common.forms.cancel') }}
        </Button>
        <Button
          :disabled="!allValid"
          @click="upgradeOrderAmount"
          class="tw-bg-primary-500 tw-text-white hover:tw-bg-primary-600"
        >
          {{ t('payments.forms.allocate_submit') }}
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watchEffect } from 'vue';
import AdjustmentTable from '@/components/shared/tables/payments-table/AdjustmentTable.vue';
import { useI18n } from 'vue-i18n';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogScrollContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

const { t, n } = useI18n();

interface Props {
  modelValue: boolean;
  paymentsData: any;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'closeAdjustmentModal'): void;
  (e: 'adjustPayments', data: any): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const paymentAdjustments = ref<any>({});

const customerId = ref<number>(0);
const validPayments = ref<any>({});
const validationMessage = t(
  'payments.titles.please_allocate_exactly_the_total_amount_of_the_payment'
);
const allValid = computed(() => {
  return Object.values(validPayments.value).every((value: any) => value === true);
});

onMounted(() => {
  if (props.paymentsData && props.paymentsData.length > 0 && props.paymentsData[0].payment) {
    customerId.value = props.paymentsData[0].payment.customer;
  }
});

const closeAdjustmentModal = () => {
  emit('update:modelValue', false);
  emit('closeAdjustmentModal');
};

const updateAdjustment = (data: any) => {
  const paymentId = data.paymentId;
  paymentAdjustments.value[paymentId] = data.orders;
  if (data.isValid) {
    validPayments.value[paymentId] = true;
  } else {
    validPayments.value[paymentId] = false;
  }
};

const upgradeOrderAmount = () => {
  emit('adjustPayments', paymentAdjustments.value);
  closeAdjustmentModal();
};
</script>
